import { useNavigate } from "react-router"
import { DynamicTable, DynamicTableDataBuilder } from "./SummaryTable"
import { ACQUISITION_FORM_LINKS } from "../links"
import { formatDate } from "../../../../../utils/date"
import { HouseholdContactFormVales } from "../contact/householdContactFormController"
import { BusinessContactFormVales } from "../contact/businessContactForm.utils"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { theme } from "../../../../../styles/stitches.config"
import {
  formatBankAccount,
  formatCircuitBreaker,
  formatEanWithoutPrefix,
  formatOptionalValue,
  formatStreet,
} from "../../acquisitionForm/format"
import {
  contractTypeOptions,
  countryOptions,
  distributionAreaOptions,
  invoicingMethodOptions,
  noticePeriodOptions,
  paymentMethodOptions,
  productionPointTypes,
} from "@nano-portal/customers"
import { AddressTable } from "./AddressTable"
import { NavigationButtons } from "../components/NavigationButtons"
import { ConsumptionPointFormValues } from "../consumptionPoint/consumptionPointForm.utils"
import { ProductionPointFormValues } from "../productionPoint/productionPointForm.utils"
import {
  ConsumptionPaymentFormValues,
  PaymentFormValues,
  hasProductionAndConsumptionBankAccounts,
  hasProductionBankAccount,
} from "../payment/paymentForm.utils"
import { ContractTypeFormValues } from "../contractType/contractTypeForm.utils"
import { CustomerType, Product } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { trpc } from "../../../../../api/trpc/trpc"
import { getConsumptionPointAddress, useTerminationFormOptions } from "../acquisitionForm.utils"
import { FormEvent } from "react"
import { useForm } from "react-hook-form"
import { isNotNil } from "@nano-portal/shared"

type Props = {
  contractTypeFormValues: ContractTypeFormValues
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  consumptionPointFormValues?: ConsumptionPointFormValues
  productionPointFormValues?: ProductionPointFormValues
  paymentFormValues: PaymentFormValues

  onSubmit?: (event?: FormEvent<HTMLFormElement>) => void
  onReturn?: () => void
}

function HouseholdContactTable(props: HouseholdContactFormVales & { product: Product }) {
  const navigate = useNavigate()

  function handleEdit() {
    navigate(`..${ACQUISITION_FORM_LINKS.CONTACT("household", props.product)}`)
  }

  return (
    <>
      <DynamicTable
        header="Kontaktní údaje"
        onEditClick={handleEdit}
        data={[
          { name: "Jméno", value: props.firstName },
          { name: "Příjmení", value: props.lastName },
          {
            name: "Datum narození",
            value: formatDate(new Date(props.birthYear, props.birthMonth, props.birthDate)),
          },
          { name: "E-mail", value: props.email },
          { name: "Telefon", value: props.phone },
        ]}
      />

      <Spacer size={theme.space.s9} />

      <AddressTable
        header="Trvalé bydliště"
        onEdit={handleEdit}
        street={props.addressStreet}
        descriptiveNumber={props.addressDescriptiveNumber}
        orientationNumber={props.addressOrientationNumber}
        city={props.addressCity}
        countryCode={props.addressCountryCode}
        postalCode={props.addressPostalCode}
      />

      {props.contactAddress && (
        <>
          <Spacer size={theme.space.s9} />
          <AddressTable
            header="Kontaktní adresa"
            onEdit={handleEdit}
            street={props.contactAddress.street}
            descriptiveNumber={props.contactAddress.descriptiveNumber}
            orientationNumber={props.contactAddress.orientationNumber}
            city={props.contactAddress.city}
            countryCode={props.contactAddress.countryCode}
            postalCode={props.contactAddress.postalCode}
          />
        </>
      )}
    </>
  )
}

function BusinessContactTable(props: BusinessContactFormVales & { product: Product }) {
  const navigate = useNavigate()

  function handleEdit() {
    navigate(`..${ACQUISITION_FORM_LINKS.CONTACT("business", props.product)}`)
  }

  return (
    <>
      <DynamicTable
        header="Kontaktní osoba"
        onEditClick={handleEdit}
        data={[
          { name: "Jméno", value: props.contactFirstName },
          { name: "Příjmení", value: props.contactLastName },
          { name: "Název firmy", value: props.companyName },
          { name: "IČO", value: props.registrationNumber },
          { name: "DIČ", value: formatOptionalValue(props.taxNumber) },
          {
            name: "Datum narození",
            value: formatDate(new Date(props.contactBirthYear, props.contactBirthMonth, props.contactBirthDate)),
          },
          { name: "E-mail", value: props.contactEmail },
          { name: "Telefon", value: props.contactPhone },
        ]}
      />
      <Spacer size={theme.space.s9} />

      <AddressTable
        header="Adresa sídla firmy"
        onEdit={handleEdit}
        street={props.addressStreet}
        descriptiveNumber={props.addressDescriptiveNumber}
        orientationNumber={props.addressOrientationNumber}
        city={props.addressCity}
        countryCode={props.addressCountryCode}
        postalCode={props.addressPostalCode}
      />

      {props.contactAddress && (
        <>
          <Spacer size={theme.space.s9} />
          <AddressTable
            header="Kontaktní adresa"
            onEdit={handleEdit}
            street={props.contactAddress.street}
            descriptiveNumber={props.contactAddress.descriptiveNumber}
            orientationNumber={props.contactAddress.orientationNumber}
            city={props.contactAddress.city}
            countryCode={props.contactAddress.countryCode}
            postalCode={props.contactAddress.postalCode}
          />
        </>
      )}
    </>
  )
}

function ContactTable(props: {
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  product: Product
}) {
  if (props.householdContactFormValues) {
    return <HouseholdContactTable {...props.householdContactFormValues} product={props.product} />
  }

  if (props.businessContactFormValues) {
    return <BusinessContactTable {...props.businessContactFormValues} product={props.product} />
  }

  return null
}

function ConsumptionPointTable(props: {
  consumptionPointFormValues: ConsumptionPointFormValues
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  product: Product
}) {
  const navigate = useNavigate()
  const address = getConsumptionPointAddress(props)
  const customerType: CustomerType = props.householdContactFormValues ? "household" : "business"
  const terminationFormOptions = useTerminationFormOptions()

  const dataBuilder = new DynamicTableDataBuilder()
  dataBuilder.addData(
    {
      name: "Ulice a č.p",
      value: formatStreet(address),
    },
    { name: "Město", value: address.city },
    {
      name: "PSČ",
      value: address.postalCode,
    },
    { name: "Země", value: countryOptions[address.countryCode] },
    { name: "EAN", value: formatEanWithoutPrefix(props.consumptionPointFormValues.ean) },
    {
      name: "Distribuční sazba",
      value: props.consumptionPointFormValues.distributionTariff,
    },
    {
      name: "Distribuční oblast",
      value: distributionAreaOptions[props.consumptionPointFormValues.distributionArea],
    },
    {
      name: "Hodnota jističe",
      value: formatCircuitBreaker({
        capacity: props.consumptionPointFormValues.circuitBreakerCapacity,
        phases: props.consumptionPointFormValues.circuitBreakerPhases,
      }),
    },
    {
      name: "Výše roční spotřeby VT",
      value: props.consumptionPointFormValues.highTariffConsumptionKWh,
    }
  )

  if (props.consumptionPointFormValues.lowTariffConsumptionKWh !== undefined) {
    dataBuilder.addData({
      name: "Výše roční spotřeby NT",
      value: props.consumptionPointFormValues.lowTariffConsumptionKWh,
    })
  }

  dataBuilder.addData(
    {
      name: "Současný dodavatel",
      value: props.consumptionPointFormValues.currentSupplier,
    },
    {
      name: "Smlouva se stávajícím dodavatelem",
      value: terminationFormOptions[props.consumptionPointFormValues.contractInfo.terminationOption],
    }
  )

  if (props.consumptionPointFormValues.contractInfo.terminationOption === "DO_NOT_TERMINATE") {
    dataBuilder.addData(
      {
        name: "Datum výpovědi současné smlouvy",
        value: formatDate(
          new Date(
            props.consumptionPointFormValues.contractInfo.contractEndYear,
            props.consumptionPointFormValues.contractInfo.contractEndMonth,
            props.consumptionPointFormValues.contractInfo.contractEndDate
          )
        ),
      },
      {
        name: "Ukončení smlouvy se stávajícím dodavatelem",
        value: props.consumptionPointFormValues.contractInfo.contractTermination?.name ?? "-",
      }
    )
  } else {
    dataBuilder.addData({
      name: "Platnost smlouvy",
      value: contractTypeOptions[props.consumptionPointFormValues.contractInfo.currentContract.contractType],
    })

    if (props.consumptionPointFormValues.contractInfo.currentContract.contractType === "VARIABLE_TERM") {
      dataBuilder.addData({
        name: "Výpovědní lhůta",
        value: noticePeriodOptions[props.consumptionPointFormValues.contractInfo.currentContract.noticePeriod],
      })
    } else {
      dataBuilder.addData(
        {
          name: "Platnost smlouvy do",
          value: formatDate(
            new Date(
              props.consumptionPointFormValues.contractInfo.currentContract.contractEndYear,
              props.consumptionPointFormValues.contractInfo.currentContract.contractEndMonth,
              props.consumptionPointFormValues.contractInfo.currentContract.contractEndDate
            )
          ),
        },

        {
          name: "Kolik dnů před ukončením smlouvy je možné nejpozději podat výpověď u vašeho stávajícího dodavatele?",
          value: formatOptionalValue(
            props.consumptionPointFormValues.contractInfo.currentContract.terminationNoticeInDaysBeforeEnd
          ),
        }
      )
    }
  }

  dataBuilder.addData({
    name: "Poslední vyúčtování",
    value: props.consumptionPointFormValues.lastBilling.name,
  })

  return (
    <DynamicTable
      header="Odběrné místo"
      onEditClick={() => navigate(`..${ACQUISITION_FORM_LINKS.CONSUMPTION_POINT(customerType, props.product)}`)}
      data={dataBuilder.build()}
    />
  )
}

function ProductionPointTable(props: {
  contractTypeFormValues: ContractTypeFormValues
  productionPointFormValues: ProductionPointFormValues
  consumptionPointFormValues?: ConsumptionPointFormValues
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  paymentFormValues: PaymentFormValues
}) {
  const navigate = useNavigate()
  const customerType: CustomerType = props.householdContactFormValues ? "household" : "business"
  const address = getConsumptionPointAddress(props)
  const bankAccount =
    hasProductionBankAccount(props.paymentFormValues) ||
    hasProductionAndConsumptionBankAccounts(props.paymentFormValues)
      ? props.paymentFormValues.productionPaymentBankAccount
      : undefined
  const ean = props.productionPointFormValues.ean ?? props.consumptionPointFormValues?.ean
  const displayAddress = props.contractTypeFormValues.product === "production"

  const terminationFormOptions = useTerminationFormOptions()
  const contractInfo =
    props.productionPointFormValues.powerStationInfo.powerStationType === "EXISTING"
      ? props.productionPointFormValues.powerStationInfo.contractInfo
      : undefined

  const dataBuilder = new DynamicTableDataBuilder()
  if (displayAddress) {
    dataBuilder.addData(
      {
        name: "Ulice a č.p",
        value: formatStreet(address),
      },
      { name: "Město", value: address.city },
      {
        name: "PSČ",
        value: address.postalCode,
      },
      { name: "Země", value: countryOptions[address.countryCode] }
    )
  }

  if (!!ean) {
    dataBuilder.addData({ name: "EAN", value: formatEanWithoutPrefix(ean) })
  }

  dataBuilder.addData(
    {
      name: "Název výrobny",
      value: props.productionPointFormValues.name,
    },
    {
      name: "Typ výrobny",
      value: productionPointTypes[props.productionPointFormValues.powerStationInfo.powerStationType],
    }
  )

  if (contractInfo) {
    dataBuilder.addData({
      name: "Smlouva se stávajícím dodavatelem",
      value: terminationFormOptions[contractInfo.terminationOption],
    })

    if (contractInfo.terminationOption === "DO_NOT_TERMINATE") {
      dataBuilder.addData(
        {
          name: "Datum výpovědi současné smlouvy",
          value: formatDate(
            new Date(contractInfo.contractEndYear, contractInfo.contractEndMonth, contractInfo.contractEndDate)
          ),
        },
        {
          name: "Ukončení smlouvy se stávajícím dodavatelem",
          value: formatOptionalValue(contractInfo.contractTermination?.name),
        }
      )
    } else {
      dataBuilder.addData(
        {
          name: "Stávající dodavatel",
          value: contractInfo.currentSupplier,
        },
        {
          name: "Platnost smlouvy",
          value: contractTypeOptions[contractInfo.currentContract.contractType],
        }
      )

      if (contractInfo.currentContract.contractType === "VARIABLE_TERM") {
        dataBuilder.addData({
          name: "Výpovědní lhůta",
          value: noticePeriodOptions[contractInfo.currentContract.noticePeriod],
        })
      } else {
        dataBuilder.addData(
          {
            name: "Platnost smlouvy do",
            value: formatDate(
              new Date(
                contractInfo.currentContract.contractEndYear,
                contractInfo.currentContract.contractEndMonth,
                contractInfo.currentContract.contractEndDate
              )
            ),
          },

          {
            name: "Kolik dnů před ukončením smlouvy je možné nejpozději podat výpověď u vašeho stávajícího dodavatele?",
            value: formatOptionalValue(contractInfo.currentContract.terminationNoticeInDaysBeforeEnd),
          }
        )
      }
    }
  } else if (props.productionPointFormValues.powerStationInfo.powerStationType === "EXISTING") {
    dataBuilder.addData({
      name: "Stávající smlouva",
      value: "Kombinovaná se smlouvou spotřební",
    })
  }

  dataBuilder.addData(
    { name: "Způsob fakturace", value: invoicingMethodOptions[props.productionPointFormValues.invoicingMethod] },
    { name: "Výkon v kWp", value: props.productionPointFormValues.powerKwp },
    {
      name: "Č. ú. pro vyúčtování",
      // eslint-disable-next-line unicorn/no-useless-undefined
      value: bankAccount ? formatBankAccount(bankAccount) : formatOptionalValue(undefined),
    },
    { name: "Protokol o PPP/SOP k výrobně", value: props.productionPointFormValues.connectionContract.name }
  )

  if (props.contractTypeFormValues.product === "production") {
    dataBuilder.addData({ name: "Vaše zpráva", value: formatOptionalValue(props.paymentFormValues.message) })
  }

  return (
    <DynamicTable
      header="Výrobní místo"
      onEditClick={() =>
        navigate(`..${ACQUISITION_FORM_LINKS.PRODUCTION_POINT(customerType, props.contractTypeFormValues.product)}`)
      }
      data={dataBuilder.build()}
    />
  )
}

function ConsumptionPaymentTable(props: {
  consumptionPaymentFormValues: ConsumptionPaymentFormValues
  customerType: CustomerType
  product: Product
}) {
  const navigate = useNavigate()
  const partnerByCodeQuery = trpc.useQuery(
    ["web.getPartnerByCode", { partnerCode: props.consumptionPaymentFormValues.partnerCode ?? "" }],
    {
      enabled: props.consumptionPaymentFormValues.partnerCode !== "",
    }
  )

  const dataBuilder = new DynamicTableDataBuilder()
  dataBuilder.addData(
    {
      name: "Forma placení záloh",
      value: paymentMethodOptions[props.consumptionPaymentFormValues.advancePaymentMethod],
    },
    {
      name: "Č. ú. pro vrácení přeplatku",
      value: formatBankAccount(props.consumptionPaymentFormValues.advancePaymentBankAccount),
    },
    { name: "Vaše zpráva", value: formatOptionalValue(props.consumptionPaymentFormValues.message) }
  )

  if (partnerByCodeQuery.data) {
    dataBuilder.addData({ name: "Kde jste se o nás dozvěděli?", value: partnerByCodeQuery.data.name })
  }

  return (
    <DynamicTable
      header="Způsob platby"
      onEditClick={() => navigate(`..${ACQUISITION_FORM_LINKS.PAYMENT(props.customerType, props.product)}`)}
      data={dataBuilder.build()}
    />
  )
}

export function SummaryForm(props: Props) {
  const form = useForm()

  function handleSubmit() {
    return props.onSubmit?.()
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <ContactTable
        businessContactFormValues={props.businessContactFormValues}
        householdContactFormValues={props.householdContactFormValues}
        product={props.contractTypeFormValues.product}
      />

      <Spacer size={theme.space.s9} />

      {props.consumptionPointFormValues && (
        <>
          <ConsumptionPointTable
            consumptionPointFormValues={props.consumptionPointFormValues}
            businessContactFormValues={props.businessContactFormValues}
            householdContactFormValues={props.householdContactFormValues}
            product={props.contractTypeFormValues.product}
          />
          <Spacer size={theme.space.s9} />
        </>
      )}

      {props.productionPointFormValues && (
        <>
          <ProductionPointTable
            contractTypeFormValues={props.contractTypeFormValues}
            productionPointFormValues={props.productionPointFormValues}
            consumptionPointFormValues={props.consumptionPointFormValues}
            householdContactFormValues={props.householdContactFormValues}
            businessContactFormValues={props.businessContactFormValues}
            paymentFormValues={props.paymentFormValues}
          />
          <Spacer size={theme.space.s9} />
        </>
      )}

      {props.consumptionPointFormValues && (
        <>
          <ConsumptionPaymentTable
            consumptionPaymentFormValues={props.paymentFormValues as ConsumptionPaymentFormValues}
            customerType={props.householdContactFormValues ? "household" : "business"}
            product={props.contractTypeFormValues.product}
          />
          <Spacer size={theme.space.s9} />
        </>
      )}

      <Spacer size={theme.space.s7} />
      <NavigationButtons
        prevText="Platební údaje"
        nextText="Odeslat"
        onReturn={props.onReturn}
        isSubmitting={form.formState.isSubmitting}
      />

      <Spacer size={theme.space.s6} />
    </form>
  )
}
